<template>
    <div class="container-fluid">
        <Breadcrumb title="Profit Report" v-bind:button-show="false"></Breadcrumb>
        <div class="row" style="position: relative">
            <div class="col-12">
                <div class="card">
                    <div class="card-body">
                        <div class="input-group form-group" v-if="this.$store.getters.isAdmin">
                            <label>User</label>
                            <treeselect placeholder="All"
                                        v-model="filter.userId"
                                        :multiple="false"
                                        :options="userList"
                                        :load-options="loadUserList"
                                        :auto-load-root-options="true"
                            />
                        </div>
                        <div class="input-group form-group">
                            <label>Report type</label>
                            <treeselect v-model="filter.groupBy" placeholder="Required"
                                        :multiple="false"
                                        :required="true"
                                        :clearable="false"
                                        :searchable="false"
                                        :options="groupByList"
                                        @select="clearReport"/>
                        </div>
                        <div class="input-group form-group">
                            <label>Campaign type</label>
                            <treeselect v-model="filter.campaignType" placeholder="All"
                                        :multiple="false"
                                        :searchable="false"
                                        :options="campaignTypeList"/>
                        </div>
                        <div class="input-group form-group">
                            <label>Date Range</label>
                            <date-picker v-model="range" range style="width:100%;" :lang="datePickerConf"></date-picker>
                        </div>
                        <div class="form-row">
                            <div class="col-2">
                                <button class="btn btn-info btn-rounded text-uppercase waves-effect waves-light w-100"
                                        @click="getProfitReport()">
                                    Get
                                </button>
                            </div>
                        </div>
                    </div>
                    <div class="card-body">
                        <div class="col-12" v-if="loading" style="min-height:100px;">
                            <Preloader v-bind:class="{preloader: loading}"></Preloader>
                        </div>
                        <div v-else>
                            <div v-if="report.length <= 1">{{emptyMessage}}</div>
                            <div v-else>
                                <div>
                                    <div class="table-responsive" v-if="filter.groupBy === 'event_date'">
                                        <table class="table">
                                            <thead>
                                            <tr>
                                                <th @click="tableOrdering('event_date')">
                                                    <span role='button'>Date</span>
                                                </th>
                                                <th @click="tableOrdering('count')">
                                                    <span role='button'>Conversions</span>
                                                </th>
                                                <th @click="tableOrdering('sum')">
                                                    <span role='button'>Profit</span>
                                                </th>
                                            </tr>
                                            </thead>
                                            <tbody>
                                            <tr v-for="(row, index) in report" :key="index">
                                                <td class="align-middle">{{row.event_date ? row.event_date : 'Total'}}</td>
                                                <td class="align-middle">{{row.count}}</td>
                                                <td class="align-middle">{{row.sum}}</td>
                                            </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                    <div class="table-responsive" v-if="filter.groupBy === 'campaign_id'">
                                        <table class="table">
                                            <thead>
                                            <tr>
                                                <th @click="tableOrdering('campaign_type')">
                                                    <span role='button'>Campaign type</span>
                                                </th>
                                                <th @click="tableOrdering('campaign_id')">
                                                    <span role='button'>Campaign ID</span>
                                                </th>
                                                <th @click="tableOrdering('count')">
                                                    <span role='button'>Conversions</span>
                                                </th>
                                                <th @click="tableOrdering('sum')">
                                                    <span role='button'>Profit</span>
                                                </th>
                                            </tr>
                                            </thead>
                                            <tbody>
                                            <tr v-for="(row, index) in report" :key="index">
                                                <td class="align-middle">
                                                    {{row.campaign_type ? getCampaignType(row.campaign_type) : 'Total'}}
                                                </td>
                                                <td class="align-middle">{{row.campaign_id}}</td>
                                                <td class="align-middle">{{row.count}}</td>
                                                <td class="align-middle">{{row.sum}}</td>
                                            </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                    <div class="table-responsive" v-if="filter.groupBy === 'country'">
                                        <table class="table">
                                            <thead>
                                            <tr>
                                                <th @click="tableOrdering('country')">
                                                    <span role='button'>Country</span>
                                                </th>
                                                <th @click="tableOrdering('count')">
                                                    <span role='button'>Conversions</span>
                                                </th>
                                                <th @click="tableOrdering('sum')">
                                                    <span role='button'>Profit</span>
                                                </th>
                                            </tr>
                                            </thead>
                                            <tbody>
                                            <tr v-for="(row, index) in report" :key="index">
                                                <td class="align-middle">
                                                    <country-flag v-bind:country=getCountryCode(row.country) size='small'/>
                                                    {{row.country ? row.country : 'Total'}}
                                                </td>
                                                <td class="align-middle">{{row.count}}</td>
                                                <td class="align-middle">{{row.sum}}</td>
                                            </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import Treeselect from '@riophae/vue-treeselect';
    import Swal from 'sweetalert2'
    import Preloader from '../components/Preloader';
    import Breadcrumb from '../components/Breadcrumb';
    import ReportService from '../services/ReportService';
    import UserService from '../services/UserService';
    import {CampaignType} from '../constants';
    import CountryFlag from 'vue-country-flag';
    let i18n = require("i18n-iso-countries");
    let i18n_locale = require("i18n-iso-countries/langs/en.json");
    import DatePicker from 'vue2-datepicker';
    import moment from 'moment';

    export default {
        name: 'ProfitReport',
        components: {
            Treeselect,
            Breadcrumb,
            Preloader,
            CountryFlag,
            DatePicker
        },
        data() {
            return {
                report: [],
                emptyMessage: 'Select dates and get a report.',
                userList: null,
                filter: {
                    userId: null,
                    fromDate: null,
                    toDate: null,
                    groupBy: 'event_date',
                    campaignType: null,
                    orderBy: 'event_date',
                    asc: true
                },
                groupByList: [
                    {id: 'event_date', label: 'Date'},
                    {id: 'campaign_id', label: 'Campaign'},
                    {id: 'country', label: 'Country'}
                ],
                campaignTypeList: CampaignType.select,
                loading: false,
                range: [moment().subtract(7, 'days').toDate(), moment().toDate()],
                datePickerConf: {
                    formatLocale: {
                        firstDayOfWeek: 1,
                    },
                },
            }
        },
        mounted() {
            i18n.registerLocale(i18n_locale);
        },
        methods: {
            loadUserList({callback}) {
                UserService.getUserSelect().then(response => {
                    this.userList = response.data['hydra:member'].map(user => {
                        return {id: user.id, label: user.username}
                    });
                    callback();
                });
            },
            getProfitReport: function () {
                this.filter.fromDate = moment(this.range[0]).format('YYYY-MM-DD');
                this.filter.toDate = moment(this.range[1]).format('YYYY-MM-DD');
                this.loading = true;
                ReportService.getProfitReport(JSON.stringify(this.filter)).then(response => {
                    if (response.status !== 200) {
                        Swal({
                            type: 'error',
                            title: 'Error',
                            text: (response.data.error === undefined) ? response.status + ': ' + response.statusText : response.data.error[0]
                        });
                        this.loading = false;
                        return;
                    }
                    this.report = response.data.report;
                    this.emptyMessage = 'No data found.';
                    this.loading = false;
                }).catch(error => {
                    Swal({
                        type: 'error',
                        title: 'Oops...',
                        text: `${error}`,
                    });
                    this.loading = false;
                });
            },
            tableOrdering(field) {
                if (this.filter.orderBy === field) {
                    this.filter.asc = !this.filter.asc;
                } else {
                    this.filter.orderBy = field;
                    this.filter.asc = false;
                }
                this.getProfitReport();
            },
            getCampaignType(type) {
                return CampaignType.value[type];
            },
            getCountryCode(country) {
                return country ? i18n.getAlpha2Code(country, 'en') : 'null';
            },
            clearReport(selected) {
                this.report = [];
                if (selected.id === 'event_date') {
                    this.filter.orderBy = 'event_date';
                } else {
                    this.filter.orderBy = 'sum';
                }
            }
        }
    }
</script>

<style scoped>
    tbody {
        font-size: 14px;
        font-family: Arial, serif;
    }

    #relay-host {
        color: #146da6;
        font-weight: 400;
    }
</style>
